import { useState, useEffect, useCallback } from "react";
import { api } from "../App";
import { getApiPrefix } from "../utils/apiHelper";
import { toast } from "sonner";
import {
  Plus, Edit2, Trash2, Car, FileSpreadsheet, Eye, X,
  Calculator, BookOpen, TrendingDown, ChevronDown, ChevronUp,
  CheckCircle2, AlertCircle
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { ExcelUploadHelper, EXCEL_COLUMNS } from "../components/ui/excel-upload-helper";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../components/ui/tooltip";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Switch } from "../components/ui/switch";
import { useTranslation } from "../contexts/I18nContext";

const fmt = (v) => parseFloat(v || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function PianoContabileView({ planId, onClose }) {
  const { t } = useTranslation();
  const [piano, setPiano] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedSections, setExpandedSections] = useState({
    riepilogo: true, piano_mensile: true, scritture: false, ammortamento: true
  });

  useEffect(() => {
    if (!planId) return;
    const fetchPiano = async () => {
      setLoading(true);
      try {
        const prefix = getApiPrefix();
        const res = await api.get(`${prefix}/leasing-plans/${planId}/piano-contabile`);
        setPiano(res.data);
      } catch {
        toast.error("Errore nel calcolo del piano contabile");
      } finally {
        setLoading(false);
      }
    };
    fetchPiano();
  }, [planId]);

  const toggleSection = (key) => setExpandedSections(s => ({ ...s, [key]: !s[key] }));

  if (loading) return <div className="py-12 text-center text-muted-foreground">Calcolo piano contabile OIC...</div>;
  if (!piano || piano.error) return <div className="py-12 text-center text-red-500">{piano?.error || "Errore"}</div>;

  const { riepilogo, piano_mensile, scritture_contabili, piano_ammortamento, piano_info } = piano;

  return (
    <div className="space-y-4" data-testid="piano-contabile-view">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-lg font-semibold" data-testid="piano-info-desc">{piano_info?.descrizione}</h3>
          <p className="text-sm text-muted-foreground">{piano_info?.veicolo} - {piano_info?.fornitore}</p>
        </div>
        <Badge variant="outline" className="text-xs">Metodo Patrimoniale OIC</Badge>
      </div>

      {/* Riepilogo */}
      <SectionToggle title="Riepilogo Contratto" icon={<BookOpen className="w-4 h-4" />} open={expandedSections.riepilogo} onToggle={() => toggleSection('riepilogo')}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="riepilogo-section">
          <MiniCard label="Costo Bene" value={fmt(riepilogo.costo_bene)} />
          <MiniCard label="Maxicanone" value={fmt(riepilogo.maxicanone_iniziale)} />
          <MiniCard label="Durata" value={`${riepilogo.durata_mesi} mesi`} />
          <MiniCard label="Canone Periodico" value={fmt(riepilogo.canone_periodico)} />
          <MiniCard label="Quota Maxicanone/mese" value={fmt(riepilogo.quota_maxicanone_mensile)} accent />
          <MiniCard label="Costo Mensile Totale" value={fmt(riepilogo.costo_mensile_totale)} accent />
          <MiniCard label="Totale Canoni" value={fmt(riepilogo.totale_canoni)} />
          <MiniCard label="Totale Esborso" value={fmt(riepilogo.totale_esborso)} highlight />
          <MiniCard label="Data Inizio" value={riepilogo.data_inizio} />
          <MiniCard label="Data Fine" value={riepilogo.data_fine} />
          <MiniCard label="Riscatto" value={riepilogo.riscatto ? "Si" : "No"} />
          {riepilogo.riscatto && <MiniCard label="Aliquota Ammort." value={`${riepilogo.aliquota_ammortamento}%`} />}
        </div>
      </SectionToggle>

      {/* Piano Mensile */}
      <SectionToggle title={`Piano Mensile (${piano_mensile?.length || 0} mesi)`} icon={<Calculator className="w-4 h-4" />} open={expandedSections.piano_mensile} onToggle={() => toggleSection('piano_mensile')}>
        <div className="border rounded-lg overflow-hidden max-h-[400px] overflow-y-auto" data-testid="piano-mensile-table">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50 sticky top-0">
                <TableHead className="w-14 text-center">#</TableHead>
                <TableHead>{t('common.month')}</TableHead>
                <TableHead className="text-right">{t('apartments.canone')}</TableHead>
                <TableHead className="text-right">Rilascio Risconto</TableHead>
                <TableHead className="text-right font-semibold">{t('common.totalCost')}</TableHead>
                <TableHead className="text-right">Risconto Residuo</TableHead>
                <TableHead className="text-right">Cumulato</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {piano_mensile?.map((m) => (
                <TableRow key={m.mese} data-testid={`piano-mese-${m.mese}`}>
                  <TableCell className="text-center font-mono text-xs text-muted-foreground">{m.mese}</TableCell>
                  <TableCell className="text-sm">{m.mese_nome}</TableCell>
                  <TableCell className="text-right font-mono text-sm">{fmt(m.canone_periodico)}</TableCell>
                  <TableCell className="text-right font-mono text-sm text-blue-600">{fmt(m.rilascio_risconto)}</TableCell>
                  <TableCell className="text-right font-mono text-sm font-semibold">{fmt(m.costo_totale_mese)}</TableCell>
                  <TableCell className="text-right font-mono text-sm text-muted-foreground">{fmt(m.risconto_attivo_residuo)}</TableCell>
                  <TableCell className="text-right font-mono text-sm text-muted-foreground">{fmt(m.costi_cumulati)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </SectionToggle>

      {/* Scritture Contabili */}
      <SectionToggle title={`Scritture Contabili (${scritture_contabili?.length || 0})`} icon={<BookOpen className="w-4 h-4" />} open={expandedSections.scritture} onToggle={() => toggleSection('scritture')}>
        <div className="space-y-2 max-h-[400px] overflow-y-auto" data-testid="scritture-section">
          {scritture_contabili?.map((s, idx) => (
            <div key={idx} className="border rounded-lg p-3 text-sm" data-testid={`scrittura-${idx}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Badge variant={s.tipo === 'apertura' ? 'default' : s.tipo === 'riscatto' ? 'destructive' : s.tipo === 'ammortamento' ? 'secondary' : 'outline'} className="text-[10px]">
                    {s.tipo}
                  </Badge>
                  <span className="font-medium">{s.descrizione}</span>
                </div>
                <span className="text-muted-foreground text-xs">{s.data}</span>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-semibold text-green-700 mb-1">DARE</p>
                  {s.dare?.map((d, di) => (
                    <div key={di} className="flex justify-between text-xs">
                      <span>{d.conto}</span>
                      <span className="font-mono">{fmt(d.importo)}</span>
                    </div>
                  ))}
                </div>
                <div>
                  <p className="text-xs font-semibold text-red-700 mb-1">AVERE</p>
                  {s.avere?.map((a, ai) => (
                    <div key={ai} className="flex justify-between text-xs">
                      <span>{a.conto}</span>
                      <span className="font-mono">{fmt(a.importo)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </SectionToggle>

      {/* Piano Ammortamento Post-Riscatto */}
      {piano_ammortamento && piano_ammortamento.length > 0 && (
        <SectionToggle title="Piano Ammortamento Post-Riscatto" icon={<TrendingDown className="w-4 h-4" />} open={expandedSections.ammortamento} onToggle={() => toggleSection('ammortamento')}>
          <div className="border rounded-lg overflow-hidden" data-testid="ammortamento-section">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="text-center">{t('common.year')}</TableHead>
                  <TableHead>Esercizio</TableHead>
                  <TableHead className="text-right">{t('common.assetValue')}</TableHead>
                  <TableHead className="text-right">Aliquota</TableHead>
                  <TableHead className="text-right">Quota Ammort.</TableHead>
                  <TableHead className="text-right">Fondo Ammort.</TableHead>
                  <TableHead className="text-right">Valore Netto</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {piano_ammortamento.map((a) => (
                  <TableRow key={a.anno} data-testid={`ammort-anno-${a.anno}`}>
                    <TableCell className="text-center font-mono">{a.anno}</TableCell>
                    <TableCell>{a.esercizio}</TableCell>
                    <TableCell className="text-right font-mono">{fmt(a.valore_bene)}</TableCell>
                    <TableCell className="text-right font-mono">{a.aliquota}%</TableCell>
                    <TableCell className="text-right font-mono text-red-600">{fmt(a.quota_ammortamento)}</TableCell>
                    <TableCell className="text-right font-mono">{fmt(a.fondo_ammortamento)}</TableCell>
                    <TableCell className="text-right font-mono font-semibold">{fmt(a.valore_netto_contabile)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </SectionToggle>
      )}
    </div>
  );
}

function SectionToggle({ title, icon, open, onToggle, children }) {
  return (
    <Card>
      <button onClick={onToggle} className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-muted/30 transition-colors rounded-t-lg" data-testid={`toggle-${title.toLowerCase().replace(/\s/g, '-')}`}>
        <div className="flex items-center gap-2 font-medium text-sm">
          {icon} {title}
        </div>
        {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      {open && <CardContent className="pt-0 pb-4">{children}</CardContent>}
    </Card>
  );
}

function MiniCard({ label, value, accent, highlight }) {
  return (
    <div className={`rounded-lg border p-2.5 ${highlight ? 'bg-red-50 border-red-200' : accent ? 'bg-blue-50 border-blue-200' : 'bg-muted/30'}`}>
      <p className="text-[11px] text-muted-foreground">{label}</p>
      <p className={`text-sm font-semibold font-mono ${highlight ? 'text-red-700' : accent ? 'text-blue-700' : ''}`}>{value}</p>
    </div>
  );
}

export default function PianiLeasingPage() {
  const { t } = useTranslation();
  const [plans, setPlans] = useState([]);
  const [automezzi, setAutomezzi] = useState([]);
  const [conti, setConti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(null);
  const [ratesDialog, setRatesDialog] = useState(null);
  const [pianoContabileId, setPianoContabileId] = useState(null);
  const [rates, setRates] = useState([]);
  const [ratesLoading, setRatesLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const emptyForm = {
    automezzo_id: "", conto_id: "", descrizione: "", fornitore: "",
    costo_bene: 0, maxicanone_iniziale: 0, durata_mesi: 24,
    canone_periodico: 0, riscatto_finale: 0, data_inizio: "",
    riscatto: false, aliquota_ammortamento: 25, note: ""
  };
  const [form, setForm] = useState(emptyForm);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const prefix = getApiPrefix();
      const [plansRes, autoRes, contiRes] = await Promise.all([
        api.get(`${prefix}/leasing-plans`),
        api.get(`${prefix}/automezzi`),
        api.get(`${prefix}/accounts`)
      ]);
      setPlans(plansRes.data);
      const autoData = Array.isArray(autoRes.data) ? autoRes.data : (autoRes.data?.automezzi || []);
      setAutomezzi(autoData);
      setConti(Array.isArray(contiRes.data) ? contiRes.data : []);
    } catch {
      toast.error("Errore nel caricamento dati");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => { setForm(emptyForm); setEditing(null); setDialogOpen(true); };

  const openEdit = (plan) => {
    setEditing(plan);
    setForm({
      automezzo_id: plan.automezzo_id || "",
      conto_id: plan.conto_id || "",
      descrizione: plan.descrizione || "",
      fornitore: plan.fornitore || "",
      costo_bene: plan.costo_bene || 0,
      maxicanone_iniziale: plan.maxicanone_iniziale || 0,
      durata_mesi: plan.durata_mesi || plan.numero_rate || 24,
      canone_periodico: plan.canone_periodico || plan.importo_canone || 0,
      riscatto_finale: plan.riscatto_finale || 0,
      data_inizio: plan.data_inizio || "",
      riscatto: !!plan.riscatto,
      aliquota_ammortamento: plan.aliquota_ammortamento || 25,
      note: plan.note || ""
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.automezzo_id || !form.conto_id || !form.descrizione || !form.data_inizio) {
      toast.error("Compila: Descrizione, Automezzo, Conto, Data Inizio");
      return;
    }
    if (!form.durata_mesi || form.durata_mesi < 1) {
      toast.error("La durata deve essere almeno 1 mese");
      return;
    }
    try {
      const prefix = getApiPrefix();
      const payload = {
        ...form,
        costo_bene: parseFloat(form.costo_bene) || 0,
        maxicanone_iniziale: parseFloat(form.maxicanone_iniziale) || 0,
        durata_mesi: parseInt(form.durata_mesi) || 24,
        canone_periodico: parseFloat(form.canone_periodico) || 0,
        riscatto_finale: parseFloat(form.riscatto_finale) || 0,
        aliquota_ammortamento: parseFloat(form.aliquota_ammortamento) || 25,
        riscatto: !!form.riscatto,
      };
      if (editing) {
        await api.put(`${prefix}/leasing-plans/${editing.id}`, payload);
      } else {
        await api.post(`${prefix}/leasing-plans`, payload);
      }
      toast.success(editing ? "Piano aggiornato" : "Piano creato con successo");
      setDialogOpen(false);
      fetchData();
    } catch {
      toast.error("Errore nel salvataggio");
    }
  };

  const handleDelete = async (id) => {
    try {
      const prefix = getApiPrefix();
      await api.delete(`${prefix}/leasing-plans/${id}`);
      toast.success("Piano eliminato");
      setDeleteConfirm(null);
      fetchData();
    } catch {
      toast.error("Errore nell'eliminazione");
    }
  };

  const handleUploadRates = async (e, planId) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(planId);
    try {
      const prefix = getApiPrefix();
      const formData = new FormData();
      formData.append("file", file);
      const res = await api.post(`${prefix}/leasing-plans/${planId}/upload-rates`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success(`${res.data.rates_count} rate caricate`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nel caricamento");
    } finally {
      setUploading(null);
    }
  };

  const viewRates = async (plan) => {
    setRatesDialog(plan);
    setRatesLoading(true);
    try {
      const prefix = getApiPrefix();
      const res = await api.get(`${prefix}/leasing-plans/${plan.id}/rates`);
      setRates(res.data);
    } catch {
      toast.error("Errore nel caricamento rate");
      setRates([]);
    } finally {
      setRatesLoading(false);
    }
  };

  const handleDeleteRates = async (planId) => {
    if (!window.confirm("Eliminare tutte le rate personalizzate?")) return;
    try {
      const prefix = getApiPrefix();
      await api.delete(`${prefix}/leasing-plans/${planId}/rates`);
      toast.success("Rate eliminate");
      setRatesDialog(null);
      setRates([]);
      fetchData();
    } catch {
      toast.error("Errore nell'eliminazione rate");
    }
  };

  const costiConti = conti.filter(c => {
    const codice = String(c.codice || '');
    return codice.startsWith('7') || codice.startsWith('5');
  });

  const pianiAttivi = plans.filter(p => p.attivo !== false).length;
  const totaleCanoniMensili = plans.reduce((s, p) => {
    const canone = parseFloat(p.canone_periodico) || parseFloat(p.importo_canone) || 0;
    const maxi = parseFloat(p.maxicanone_iniziale) || 0;
    const durata = parseInt(p.durata_mesi) || parseInt(p.numero_rate) || 1;
    const quotaMaxi = durata > 0 ? maxi / durata : 0;
    return s + canone + quotaMaxi;
  }, 0);
  const totaleEsborso = plans.reduce((s, p) => {
    const canone = parseFloat(p.canone_periodico) || parseFloat(p.importo_canone) || 0;
    const durata = parseInt(p.durata_mesi) || parseInt(p.numero_rate) || 1;
    const maxi = parseFloat(p.maxicanone_iniziale) || 0;
    const risc = p.riscatto ? (parseFloat(p.riscatto_finale) || 0) : 0;
    return s + (canone * durata) + maxi + risc;
  }, 0);

  // Computed preview for form
  const quotaMaxiMensile = form.durata_mesi > 0 ? parseFloat(form.maxicanone_iniziale || 0) / parseInt(form.durata_mesi) : 0;
  const costoMensileTotale = parseFloat(form.canone_periodico || 0) + quotaMaxiMensile;

  return (
    <div className="space-y-6" data-testid="piani-leasing-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" data-testid="page-title">{t("leasing.title")}</h1>
          <p className="text-sm text-muted-foreground mt-1">Gestione contabile leasing auto - Metodo Patrimoniale OIC</p>
        </div>
        <Button onClick={openCreate} data-testid="create-leasing-btn">
          <Plus className="w-4 h-4 mr-2" /> Nuovo Piano
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card data-testid="stat-piani-attivi">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">Piani Attivi</CardTitle></CardHeader>
          <CardContent><div className="text-2xl font-bold">{pianiAttivi}</div></CardContent>
        </Card>
        <Card data-testid="stat-costo-mensile">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">{t('common.totalMonthlyCost')}</CardTitle></CardHeader>
          <CardContent><div className="text-2xl font-bold text-blue-600">{fmt(totaleCanoniMensili)}</div><p className="text-[11px] text-muted-foreground">Canone + Quota Maxicanone</p></CardContent>
        </Card>
        <Card data-testid="stat-veicoli">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">Veicoli in Leasing</CardTitle></CardHeader>
          <CardContent><div className="text-2xl font-bold">{new Set(plans.map(p => p.automezzo_id)).size}</div></CardContent>
        </Card>
        <Card data-testid="stat-esborso-totale">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-medium text-muted-foreground">Esborso Totale Contratti</CardTitle></CardHeader>
          <CardContent><div className="text-2xl font-bold text-red-600">{fmt(totaleEsborso)}</div></CardContent>
        </Card>
      </div>

      {/* Plans Table */}
      <Card>
        <CardContent className="p-0">
          <Table data-testid="leasing-table">
            <TableHeader>
              <TableRow>
                <TableHead>{t('common.vehicle')}</TableHead>
                <TableHead>{t('common.description')}</TableHead>
                <TableHead>{t('common.supplier')}</TableHead>
                <TableHead className="text-right">{t('apartments.canone')}</TableHead>
                <TableHead className="text-right">Maxicanone</TableHead>
                <TableHead className="text-center">Durata</TableHead>
                <TableHead className="text-right">Costo/Mese</TableHead>
                <TableHead className="text-center">Riscatto</TableHead>
                <TableHead>{t('common.status')}</TableHead>
                <TableHead className="text-right">{t('common.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={10} className="text-center py-8 text-muted-foreground">{t('common.loading')}</TableCell></TableRow>
              ) : plans.length === 0 ? (
                <TableRow><TableCell colSpan={10} className="text-center py-8 text-muted-foreground">Nessun piano leasing</TableCell></TableRow>
              ) : plans.map(plan => {
                const canone = parseFloat(plan.canone_periodico) || parseFloat(plan.importo_canone) || 0;
                const maxi = parseFloat(plan.maxicanone_iniziale) || 0;
                const durata = parseInt(plan.durata_mesi) || parseInt(plan.numero_rate) || 1;
                const costoMese = canone + (durata > 0 ? maxi / durata : 0);
                return (
                  <TableRow key={plan.id} data-testid={`leasing-row-${plan.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Car className="w-4 h-4 text-muted-foreground" />
                        <span className="font-medium">{plan.targa || 'N/D'}</span>
                        <span className="text-xs text-muted-foreground">{plan.marca} {plan.modello}</span>
                      </div>
                    </TableCell>
                    <TableCell className="max-w-[180px] truncate">{plan.descrizione}</TableCell>
                    <TableCell>{plan.fornitore || '-'}</TableCell>
                    <TableCell className="text-right font-mono">{fmt(canone)}</TableCell>
                    <TableCell className="text-right font-mono text-blue-600">{maxi > 0 ? fmt(maxi) : '-'}</TableCell>
                    <TableCell className="text-center">{durata} mesi</TableCell>
                    <TableCell className="text-right font-mono font-semibold">{fmt(costoMese)}</TableCell>
                    <TableCell className="text-center">
                      {plan.riscatto ? (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger>
                              <Badge variant="outline" className="border-green-400 text-green-700 text-[10px]">
                                <CheckCircle2 className="w-3 h-3 mr-0.5" /> Si
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent>Riscatto: {fmt(plan.riscatto_finale)} - Ammort. {plan.aliquota_ammortamento}%</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      ) : (
                        <span className="text-xs text-muted-foreground">No</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={plan.attivo !== false ? "default" : "secondary"} data-testid={`leasing-status-${plan.id}`}>
                        {plan.attivo !== false ? "Attivo" : "Chiuso"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-1 justify-end">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button variant="ghost" size="icon" onClick={() => setPianoContabileId(plan.id)} data-testid={`view-piano-${plan.id}`}>
                                <Calculator className="w-4 h-4 text-purple-600" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Piano Contabile OIC</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        {plan.has_custom_rates ? (
                          <Button variant="ghost" size="icon" onClick={() => viewRates(plan)} data-testid={`view-rates-${plan.id}`}>
                            <Eye className="w-4 h-4 text-blue-500" />
                          </Button>
                        ) : (
                          <ExcelUploadHelper
                            columns={EXCEL_COLUMNS.leasing_rates}
                            title="Struttura Excel - Rate Leasing"
                            buttonText="Importa"
                            buttonVariant="ghost"
                            buttonSize="icon"
                            uploading={uploading === plan.id}
                            onUpload={(e) => handleUploadRates(e, plan.id)}
                          >
                            <FileSpreadsheet className="w-4 h-4 text-green-600" />
                          </ExcelUploadHelper>
                        )}
                        <Button variant="ghost" size="icon" onClick={() => openEdit(plan)} data-testid={`edit-leasing-${plan.id}`}>
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => setDeleteConfirm(plan.id)} data-testid={`delete-leasing-${plan.id}`}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Dialog Crea/Modifica Piano OIC */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="leasing-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Modifica Piano Leasing" : "Nuovo Piano Leasing"}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Sezione 1: Dati Generali */}
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Dati Generali</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Label>Descrizione *</Label>
                  <Input value={form.descrizione} onChange={e => setForm(f => ({...f, descrizione: e.target.value}))} placeholder="Es: Leasing Fiat Ducato XL" data-testid="input-descrizione" />
                </div>
                <div>
                  <Label>Automezzo *</Label>
                  <Select value={form.automezzo_id} onValueChange={v => setForm(f => ({...f, automezzo_id: v}))}>
                    <SelectTrigger data-testid="select-automezzo"><SelectValue placeholder="Seleziona veicolo" /></SelectTrigger>
                    <SelectContent>
                      {automezzi.map(a => (
                        <SelectItem key={a.id} value={a.id}>{a.targa} - {a.marca} {a.modello}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Conto Contabile *</Label>
                  <Select value={form.conto_id} onValueChange={v => setForm(f => ({...f, conto_id: v}))}>
                    <SelectTrigger data-testid="select-conto"><SelectValue placeholder={t("dialogs.selectAccount")} /></SelectTrigger>
                    <SelectContent>
                      {costiConti.map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.codice} - {c.nome}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Fornitore / Societa Leasing</Label>
                  <Input value={form.fornitore} onChange={e => setForm(f => ({...f, fornitore: e.target.value}))} placeholder="Es: Arval, ALD, Leasys" data-testid="input-fornitore" />
                </div>
                <div>
                  <Label>Data Inizio Contratto *</Label>
                  <Input type="date" value={form.data_inizio} onChange={e => setForm(f => ({...f, data_inizio: e.target.value}))} data-testid="input-data-inizio" />
                </div>
              </div>
            </div>

            {/* Sezione 2: Parametri Economici */}
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Parametri Economici</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Costo del Bene</Label>
                  <Input type="number" step="0.01" value={form.costo_bene} onChange={e => setForm(f => ({...f, costo_bene: e.target.value}))} placeholder="Valore originale del veicolo" data-testid="input-costo-bene" />
                </div>
                <div>
                  <Label>Maxicanone Iniziale</Label>
                  <Input type="number" step="0.01" value={form.maxicanone_iniziale} onChange={e => setForm(f => ({...f, maxicanone_iniziale: e.target.value}))} placeholder="Risconto attivo" data-testid="input-maxicanone" />
                </div>
                <div>
                  <Label>Durata (mesi) *</Label>
                  <Input type="number" min="1" value={form.durata_mesi} onChange={e => setForm(f => ({...f, durata_mesi: e.target.value}))} data-testid="input-durata" />
                </div>
                <div>
                  <Label>Canone Periodico (mensile)</Label>
                  <Input type="number" step="0.01" value={form.canone_periodico} onChange={e => setForm(f => ({...f, canone_periodico: e.target.value}))} data-testid="input-canone" />
                </div>
                <div>
                  <Label>Valore di Riscatto</Label>
                  <Input type="number" step="0.01" value={form.riscatto_finale} onChange={e => setForm(f => ({...f, riscatto_finale: e.target.value}))} data-testid="input-riscatto" />
                </div>
              </div>
            </div>

            {/* Sezione 3: Riscatto */}
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Opzione Riscatto</p>
              <div className="flex items-center gap-3 p-3 border rounded-lg">
                <Switch checked={form.riscatto} onCheckedChange={v => setForm(f => ({...f, riscatto: v}))} data-testid="switch-riscatto" />
                <div>
                  <p className="text-sm font-medium">Previsto riscatto del bene</p>
                  <p className="text-xs text-muted-foreground">Se attivo, a fine contratto il bene viene iscritto come cespite al valore di riscatto</p>
                </div>
              </div>
              {form.riscatto && (
                <div className="mt-3">
                  <Label>Aliquota Ammortamento Post-Riscatto (%)</Label>
                  <Input type="number" step="0.01" min="0" max="100" value={form.aliquota_ammortamento} onChange={e => setForm(f => ({...f, aliquota_ammortamento: e.target.value}))} data-testid="input-aliquota" />
                  <p className="text-[11px] text-muted-foreground mt-1">Primo anno: meta aliquota (prassi italiana)</p>
                </div>
              )}
            </div>

            {/* Anteprima Calcolo */}
            <div className="bg-muted/40 border rounded-lg p-3 space-y-1" data-testid="preview-calcolo">
              <p className="text-xs font-semibold uppercase text-muted-foreground">Anteprima Calcolo OIC</p>
              <div className="grid grid-cols-3 gap-2 text-sm">
                <div>
                  <span className="text-muted-foreground text-xs">Quota Maxicanone/mese</span>
                  <p className="font-mono font-semibold text-blue-700">{fmt(quotaMaxiMensile)}</p>
                </div>
                <div>
                  <span className="text-muted-foreground text-xs">{t('common.totalMonthlyCost')}</span>
                  <p className="font-mono font-semibold">{fmt(costoMensileTotale)}</p>
                </div>
                <div>
                  <span className="text-muted-foreground text-xs">Totale Esborso</span>
                  <p className="font-mono font-semibold text-red-700">
                    {fmt((parseFloat(form.canone_periodico) || 0) * (parseInt(form.durata_mesi) || 0) + (parseFloat(form.maxicanone_iniziale) || 0) + (form.riscatto ? (parseFloat(form.riscatto_finale) || 0) : 0))}
                  </p>
                </div>
              </div>
            </div>

            {/* Note */}
            <div>
              <Label>{t('common.notes')}</Label>
              <Input value={form.note || ""} onChange={e => setForm(f => ({...f, note: e.target.value}))} placeholder="Note aggiuntive" data-testid="input-note" />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleSave} data-testid="save-leasing-btn">{editing ? "Aggiorna" : "Crea Piano"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Piano Contabile OIC */}
      <Dialog open={!!pianoContabileId} onOpenChange={(open) => { if (!open) setPianoContabileId(null); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="piano-contabile-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calculator className="w-5 h-5 text-purple-600" />
              Piano Contabile OIC - Metodo Patrimoniale
            </DialogTitle>
          </DialogHeader>
          {pianoContabileId && <PianoContabileView planId={pianoContabileId} onClose={() => setPianoContabileId(null)} />}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPianoContabileId(null)}>{t('common.close')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Rate Custom */}
      <Dialog open={!!ratesDialog} onOpenChange={(open) => { if (!open) { setRatesDialog(null); setRates([]); } }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="rates-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-blue-600" />
              Rate Variabili - {ratesDialog?.descrizione}
            </DialogTitle>
          </DialogHeader>
          {ratesDialog && (
            <div className="space-y-4">
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-4">
                  <span className="text-muted-foreground">Veicolo: <strong>{ratesDialog.targa}</strong></span>
                  <span className="text-muted-foreground">Rate: <strong>{rates.length}</strong></span>
                  <span className="text-muted-foreground">Totale: <strong className="text-red-600">{fmt(rates.reduce((s, r) => s + (parseFloat(r.importo) || 0), 0))}</strong></span>
                </div>
                <div className="flex items-center gap-2">
                  <ExcelUploadHelper
                    columns={EXCEL_COLUMNS.leasing_rates}
                    title="Sostituisci Rate"
                    buttonText="Sostituisci Excel"
                    buttonVariant="outline"
                    buttonSize="sm"
                    uploading={uploading === ratesDialog.id}
                    onUpload={(e) => {
                      handleUploadRates(e, ratesDialog.id).then(() => viewRates(ratesDialog));
                    }}
                  />
                  <Button variant="destructive" size="sm" onClick={() => handleDeleteRates(ratesDialog.id)} data-testid="delete-all-rates-btn">
                    <Trash2 className="w-3.5 h-3.5 mr-1.5" />{t('common.delete')}</Button>
                </div>
              </div>
              <div className="border rounded-lg overflow-hidden">
                <Table data-testid="rates-table">
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead className="w-16 text-center">#</TableHead>
                      <TableHead>{t('invoices.dueDate')}</TableHead>
                      <TableHead className="text-right">{t('common.amount')}</TableHead>
                      <TableHead className="text-right">Quota Capitale</TableHead>
                      <TableHead className="text-right">Quota Interessi</TableHead>
                      <TableHead>{t('common.notes')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {ratesLoading ? (
                      <TableRow><TableCell colSpan={6} className="text-center py-6 text-muted-foreground">{t('common.loading')}</TableCell></TableRow>
                    ) : rates.length === 0 ? (
                      <TableRow><TableCell colSpan={6} className="text-center py-6 text-muted-foreground">Nessuna rata</TableCell></TableRow>
                    ) : rates.map((rate, idx) => (
                      <TableRow key={rate.id || idx} data-testid={`rate-row-${idx}`}>
                        <TableCell className="text-center text-muted-foreground font-mono text-xs">{rate.numero_rata}</TableCell>
                        <TableCell>{rate.data_scadenza || '-'}</TableCell>
                        <TableCell className="text-right font-mono font-medium">{fmt(rate.importo)}</TableCell>
                        <TableCell className="text-right font-mono text-muted-foreground">{rate.quota_capitale ? fmt(rate.quota_capitale) : '-'}</TableCell>
                        <TableCell className="text-right font-mono text-muted-foreground">{rate.quota_interessi ? fmt(rate.quota_interessi) : '-'}</TableCell>
                        <TableCell className="text-xs text-muted-foreground max-w-[150px] truncate">{rate.note || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRatesDialog(null); setRates([]); }}>{t('common.close')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={(open) => !open && setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Conferma Eliminazione</AlertDialogTitle>
            <AlertDialogDescription>Eliminare questo piano di leasing? L'operazione non puo essere annullata.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-leasing">{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleDelete(deleteConfirm)} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-leasing">{t('common.delete')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
